/**
 * PresetSelector Component
 *
 * Dropdown that lets the user pick:
 *  - a Stock Profile (Growth / Value / Income) from the CC Score v6 classifier
 *  - one of their own saved custom presets
 *  - "Custom" (clears profile and preset)
 *
 * The 4 legacy system presets (blue_chip / prime_liquidity / high_volatility /
 * deep_value) are no longer surfaced — profiles replaced them.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  ChevronDownIcon,
  SparklesIcon,
  CheckIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { Preset } from '../../types/presets';
import { usePresets } from '../../hooks/usePresets';
import './PresetSelector.css';

interface PresetSelectorProps {
  activePresetId: number | null;
  onSelectPreset: (preset: Preset | null) => void;
  onSavePreset?: () => void;
  showSaveButton?: boolean;
  // Modification tracking (custom presets only)
  isModified?: boolean;
  basePresetId?: number | null;
}

const PresetSelector: React.FC<PresetSelectorProps> = ({
  activePresetId,
  onSelectPreset,
  onSavePreset,
  showSaveButton = true,
  isModified = false,
  basePresetId = null
}) => {
  const { presets, userPresets, isLoading } = usePresets();
  const [isOpen, setIsOpen] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Stop pulse animation after 4 seconds or when user interacts
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPulse(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  // Stop pulse when preset or profile is selected
  useEffect(() => {
    if (activePresetId !== null) {
      setShowPulse(false);
    }
  }, [activePresetId]);

  // Find active custom preset
  const activePreset = activePresetId
    ? presets.find(p => p.id === activePresetId)
    : null;

  const basePreset = basePresetId
    ? presets.find(p => p.id === basePresetId)
    : null;

  // Determine what to display in the trigger button
  // Priority: active preset > modified base preset > nothing
  const displayPreset = activePreset || (isModified ? basePreset : null);
  const displayColor = displayPreset ? '#6B7280' : undefined;
  const hasSelection = !!displayPreset;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectPreset = (preset: Preset | null) => {
    onSelectPreset(preset);
    setIsOpen(false);
  };

  return (
    <div className="preset-selector" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        className={`preset-selector-trigger ${hasSelection ? 'has-preset' : ''} ${isModified ? 'is-modified' : ''} ${showPulse && !hasSelection ? 'pulse-attention' : ''}`}
        onClick={() => { setIsOpen(!isOpen); setShowPulse(false); }}
        style={displayColor ? {
          borderColor: isModified ? '#6B7280' : displayColor,
          boxShadow: isModified ? 'none' : `0 0 12px ${displayColor}30`
        } : undefined}
      >
        {displayPreset ? (
          <>
            <span
              className="preset-indicator"
              style={{ backgroundColor: '#6B7280' }}
            />
            <span className="preset-name">
              {isModified ? 'Custom (Modified)' : displayPreset.name}
            </span>
          </>
        ) : (
          <span className="preset-name">Select Preset</span>
        )}
        <ChevronDownIcon className={`chevron-icon ${isOpen ? 'open' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="preset-dropdown">
          {/* Custom Option — clears active preset */}
          <button
            className={`preset-option ${!activePreset ? 'active' : ''}`}
            onClick={() => handleSelectPreset(null)}
          >
            <span className="preset-option-content">
              <span className="preset-indicator custom" />
              <span className="preset-option-name">Custom</span>
            </span>
            {!activePreset && <CheckIcon className="check-icon" />}
          </button>

          {/* User Presets */}
          {userPresets.length > 0 && (
            <>
              <div className="preset-section-header">My Presets</div>
              {userPresets.map(preset => (
                <button
                  key={preset.id}
                  className={`preset-option ${activePresetId === preset.id ? 'active' : ''}`}
                  onClick={() => handleSelectPreset(preset)}
                >
                  <span className="preset-option-content">
                    <span className="preset-indicator user" />
                    <SparklesIcon className="preset-icon" />
                    <span className="preset-option-text">
                      <span className="preset-option-name">{preset.name}</span>
                    </span>
                  </span>
                  {activePresetId === preset.id && <CheckIcon className="check-icon" />}
                </button>
              ))}
            </>
          )}

          {/* Save Preset Button */}
          {showSaveButton && onSavePreset && (
            <>
              <div className="preset-divider" />
              <button
                className="preset-option save-preset-btn"
                onClick={() => {
                  setIsOpen(false);
                  onSavePreset();
                }}
              >
                <span className="preset-option-content">
                  <PlusIcon className="plus-icon" />
                  <span className="preset-option-name">Save Current as Preset</span>
                </span>
              </button>
            </>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="preset-loading">Loading presets...</div>
          )}
        </div>
      )}
    </div>
  );
};

export default PresetSelector;
